import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Button, 
  CircularProgress, 
  Container,
  Grid,
  Paper,
  TextField,
  Typography,
  IconButton,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { toast } from 'react-hot-toast';
import { SyllabusService } from '../../../../services/SyllabusService';
import { useAppSelector } from '../../../../state/state';
import { selectEffectiveUID } from '../../../../state/UserStatesV2/userSliceV2';
import { 
  Syllabus, 
  Level, 
  Session, 
  Topic, 
  Subtopic,
} from '../../../../state/subscriptionTableState/subscriptionTableSlice';
import { v4 as uuidv4 } from 'uuid';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(1),
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
}));

interface SyllabusEditorPageProps {
  isCreate?: boolean;
}

export default function SyllabusEditorPage({ isCreate }: SyllabusEditorPageProps) {
  const { syllabusId } = useParams<{ syllabusId: string }>();
  const navigate = useNavigate();
  const serviceProviderId = useAppSelector(selectEffectiveUID);
  
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [isModified, setIsModified] = useState<boolean>(false);
  const [syllabusData, setSyllabusData] = useState<Syllabus>({
    id: '',
    name: '',
    description: '',
    levels: [],
    createdAt: Date.now(),
    lastModified: Date.now()
  });

  // State for new items
  const [newLevelName, setNewLevelName] = useState<string>('');
  const [newSession, setNewSession] = useState<{ levelId: string; name: string; } | null>(null);
  const [newTopic, setNewTopic] = useState<{ levelId: string; sessionId: string; name: string; } | null>(null);
  const [newSubtopic, setNewSubtopic] = useState<{ levelId: string; sessionId: string; topicId: string; name: string; } | null>(null);

  // Expanded states for accordions
  const [expandedLevel, setExpandedLevel] = useState<string | null>(null);

  // Add unsaved changes warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isModified) {
        const message = 'You have unsaved changes. Are you sure you want to leave?';
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isModified]);

  useEffect(() => {
    const loadSyllabus = async () => {
      if (!serviceProviderId) {
        console.error("Service provider ID is not available. Cannot load syllabus.");
        toast.error('User authentication issue. Please try logging out and back in.');
        setLoading(false);
        return;
      }
      
      if (!isCreate && syllabusId) {
        try {
          setLoading(true);
          console.log(`Loading syllabus ${syllabusId} for provider ${serviceProviderId}`);
          const data = await SyllabusService.getSyllabus(serviceProviderId, syllabusId);
          if (data) {
            console.log("Successfully loaded syllabus data:", data);
            setSyllabusData(data);
          } else {
            console.error(`Syllabus with ID ${syllabusId} not found`);
            toast.error('Syllabus not found');
            navigate('/settings');
          }
        } catch (error) {
          console.error('Error loading syllabus:', error);
          toast.error('Failed to load syllabus data: ' + (error instanceof Error ? error.message : 'Unknown error'));
          navigate('/settings');
        } finally {
          setLoading(false);
        }
      } else {
        // Creating a new syllabus
        console.log("Creating a new syllabus instance");
        setSyllabusData({
          id: uuidv4(),
          name: '',
          description: '',
          levels: [],
          createdAt: Date.now(),
          lastModified: Date.now()
        });
        setLoading(false);
      }
    };

    loadSyllabus();
  }, [syllabusId, isCreate, serviceProviderId, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSyllabusData(prev => ({
      ...prev,
      [name]: value
    }));
    setIsModified(true);
  };

  const handleAddLevel = () => {
    if (!newLevelName.trim()) {
      toast.error('Please enter a name for the level');
      return;
    }

    const newLevel: Level = {
      id: uuidv4(),
      name: newLevelName,
      description: '',
      order: syllabusData.levels.length + 1,
      sessions: [],
      status: 'not_started'
    };

    setSyllabusData(prev => ({
      ...prev,
      levels: [...prev.levels, newLevel],
      lastModified: Date.now()
    }));

    setNewLevelName('');
    setExpandedLevel(newLevel.id);
    setIsModified(true);
    toast.success('Level added successfully');
  };

  const handleAddSession = (levelId: string) => {
    if (!newSession || !newSession.name.trim()) {
      toast.error('Please enter a name for the session');
      return;
    }

    const levelIndex = syllabusData.levels.findIndex(level => level.id === levelId);
    
    if (levelIndex === -1) {
      toast.error('Level not found');
      return;
    }

    const newSessionObj: Session = {
      id: uuidv4(),
      name: newSession.name,
      description: '',
      order: syllabusData.levels[levelIndex].sessions.length + 1,
      topics: [],
      status: 'not_started'
    };

    const updatedLevels = [...syllabusData.levels];
    updatedLevels[levelIndex].sessions.push(newSessionObj);

    setSyllabusData(prev => ({
      ...prev,
      levels: updatedLevels,
      lastModified: Date.now()
    }));

    setNewSession(null);
    setIsModified(true);
    toast.success('Session added successfully');
  };

  const handleAddTopic = (levelId: string, sessionId: string) => {
    if (!newTopic || !newTopic.name.trim()) {
      toast.error('Please enter a name for the topic');
      return;
    }

    const levelIndex = syllabusData.levels.findIndex(l => l.id === levelId);
    if (levelIndex === -1) return;

    const sessionIndex = syllabusData.levels[levelIndex].sessions.findIndex(s => s.id === sessionId);
    if (sessionIndex === -1) return;

    const newTopicObj: Topic = {
      id: uuidv4(),
      name: newTopic.name,
      description: '',
      order: syllabusData.levels[levelIndex].sessions[sessionIndex].topics?.length + 1 || 1,
      subtopics: [],
      status: 'not_started'
    };

    const updatedLevels = [...syllabusData.levels];
    if (!updatedLevels[levelIndex].sessions[sessionIndex].topics) {
      updatedLevels[levelIndex].sessions[sessionIndex].topics = [];
    }

    updatedLevels[levelIndex].sessions[sessionIndex].topics.push(newTopicObj);
    setSyllabusData({
      ...syllabusData,
      levels: updatedLevels,
      lastModified: Date.now()
    });

    setNewTopic(null);
    setIsModified(true);
    toast.success('Topic added successfully');
  };

  const handleAddSubtopic = (levelId: string, sessionId: string, topicId: string) => {
    if (!newSubtopic || !newSubtopic.name.trim()) {
      toast.error('Please enter a name for the subtopic');
      return;
    }

    const levelIndex = syllabusData.levels.findIndex(level => level.id === levelId);
    if (levelIndex === -1) return;

    const sessionIndex = syllabusData.levels[levelIndex].sessions.findIndex(session => session.id === sessionId);
    if (sessionIndex === -1) return;

    const topicIndex = syllabusData.levels[levelIndex].sessions[sessionIndex].topics.findIndex(topic => topic.id === topicId);
    if (topicIndex === -1) return;

    const newSubtopicObj: Subtopic = {
      id: uuidv4(),
      name: newSubtopic.name,
      description: '',
      order: syllabusData.levels[levelIndex].sessions[sessionIndex].topics[topicIndex].subtopics?.length + 1 || 1,
      status: 'not_started'
    };

    const updatedLevels = [...syllabusData.levels];
    if (!updatedLevels[levelIndex].sessions[sessionIndex].topics[topicIndex].subtopics) {
      updatedLevels[levelIndex].sessions[sessionIndex].topics[topicIndex].subtopics = [];
    }
    updatedLevels[levelIndex].sessions[sessionIndex].topics[topicIndex].subtopics.push(newSubtopicObj);

    setSyllabusData(prev => ({
      ...prev,
      levels: updatedLevels,
      lastModified: Date.now()
    }));

    setNewSubtopic(null);
    setIsModified(true);
    toast.success('Subtopic added successfully');
  };

  const handleDeleteLevel = (levelId: string) => {
    if (window.confirm('Are you sure you want to delete this level and all its content?')) {
      setSyllabusData(prev => ({
        ...prev,
        levels: prev.levels.filter(level => level.id !== levelId),
        lastModified: Date.now()
      }));
      setIsModified(true);
      toast.success('Level deleted successfully');
    }
  };

  const handleDeleteSession = (levelId: string, sessionId: string) => {
    if (window.confirm('Are you sure you want to delete this session and all its content?')) {
      const levelIndex = syllabusData.levels.findIndex(level => level.id === levelId);
      if (levelIndex === -1) return;

      const updatedLevels = [...syllabusData.levels];
      updatedLevels[levelIndex].sessions = updatedLevels[levelIndex].sessions.filter(session => session.id !== sessionId);

      setSyllabusData(prev => ({
        ...prev,
        levels: updatedLevels,
        lastModified: Date.now()
      }));
      setIsModified(true);
      toast.success('Session deleted successfully');
    }
  };

  const handleDeleteTopic = (levelId: string, sessionId: string, topicId: string) => {
    if (window.confirm('Are you sure you want to delete this topic and all its content?')) {
      const levelIndex = syllabusData.levels.findIndex(level => level.id === levelId);
      if (levelIndex === -1) return;

      const sessionIndex = syllabusData.levels[levelIndex].sessions.findIndex(session => session.id === sessionId);
      if (sessionIndex === -1) return;

      const updatedLevels = [...syllabusData.levels];
      updatedLevels[levelIndex].sessions[sessionIndex].topics = updatedLevels[levelIndex].sessions[sessionIndex].topics.filter(topic => topic.id !== topicId);

      setSyllabusData(prev => ({
        ...prev,
        levels: updatedLevels,
        lastModified: Date.now()
      }));
      setIsModified(true);
      toast.success('Topic deleted successfully');
    }
  };

  const handleDeleteSubtopic = (levelId: string, sessionId: string, topicId: string, subtopicId: string) => {
    if (window.confirm('Are you sure you want to delete this subtopic?')) {
      const levelIndex = syllabusData.levels.findIndex(level => level.id === levelId);
      if (levelIndex === -1) return;

      const sessionIndex = syllabusData.levels[levelIndex].sessions.findIndex(session => session.id === sessionId);
      if (sessionIndex === -1) return;

      const topicIndex = syllabusData.levels[levelIndex].sessions[sessionIndex].topics.findIndex(topic => topic.id === topicId);
      if (topicIndex === -1) return;

      const updatedLevels = [...syllabusData.levels];
      updatedLevels[levelIndex].sessions[sessionIndex].topics[topicIndex].subtopics = 
        updatedLevels[levelIndex].sessions[sessionIndex].topics[topicIndex].subtopics.filter(subtopic => subtopic.id !== subtopicId);

      setSyllabusData(prev => ({
        ...prev,
        levels: updatedLevels,
        lastModified: Date.now()
      }));
      setIsModified(true);
      toast.success('Subtopic deleted successfully');
    }
  };

  const handleSave = async () => {
    if (!syllabusData.name) {
      toast.error('Please enter a name for your syllabus');
      return;
    }

    try {
      setSaving(true);
      
      // Log the service provider ID to verify it's correct
      console.log("Service Provider ID:", serviceProviderId);
      
      // Make sure all levels, sessions, topics, and subtopics are properly structured
      const processedLevels = syllabusData.levels.map(level => ({
        ...level,
        sessions: (level.sessions || []).map(session => ({
          ...session,
          topics: (session.topics || []).map(topic => ({
            ...topic,
            subtopics: topic.subtopics || []
          }))
        }))
      }));
      
      if (isCreate) {
        // For new syllabi, create a document in Firebase
        console.log("Creating new syllabus with data:", JSON.stringify({
          ...syllabusData,
          levels: processedLevels
        }));
        
        if (!serviceProviderId) {
          throw new Error('Service Provider ID is missing or invalid');
        }
        
        const newSyllabus = await SyllabusService.createSyllabus(serviceProviderId, {
          name: syllabusData.name,
          description: syllabusData.description || '',
          levels: processedLevels,
          createdAt: syllabusData.createdAt,
          lastModified: Date.now()
        });
        
        console.log("Created new syllabus:", newSyllabus);
        toast.success('Syllabus created successfully');
        
        // Navigate back to settings page after successful creation
        navigate('/settings', { replace: true });
      } else {
        // For existing syllabi, update the document in Firebase
        if (!syllabusId) {
          throw new Error('Syllabus ID is missing');
        }
        console.log("Updating existing syllabus ID:", syllabusId);
        console.log("Update data:", JSON.stringify({
          name: syllabusData.name,
          description: syllabusData.description || '',
          levels: processedLevels,
          lastModified: Date.now()
        }));
        
        if (!serviceProviderId) {
          throw new Error('Service Provider ID is missing or invalid');
        }
        
        await SyllabusService.updateSyllabus(serviceProviderId, syllabusId, {
          name: syllabusData.name,
          description: syllabusData.description || '',
          levels: processedLevels,
          lastModified: Date.now()
        });
        
        console.log("Updated syllabus successfully");
        toast.success('Syllabus updated successfully');
        
        // Refresh the data to show the latest version
        const updatedSyllabus = await SyllabusService.getSyllabus(serviceProviderId, syllabusId);
        if (updatedSyllabus) {
          console.log("Reloaded updated syllabus:", updatedSyllabus);
          setSyllabusData(updatedSyllabus);
        } else {
          console.warn("Could not reload syllabus after update");
          // If we couldn't reload the data, navigate back to the settings page
          navigate('/settings');
        }
      }

      // After successful save, reset the modified flag
      setIsModified(false);
      
    } catch (error) {
      console.error('Error saving syllabus:', error);
      toast.error('Failed to save syllabus: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  // Handle accordion expansion
  const handleLevelExpand = (levelId: string) => {
    setExpandedLevel(expandedLevel === levelId ? null : levelId);
  };

  // Topic expand state is managed internally by the UI now

  // Add a navigation handler that warns about unsaved changes
  const handleNavigateAway = () => {
    if (isModified) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave this page?')) {
        navigate('/settings');
      }
    } else {
      navigate('/settings');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            onClick={handleNavigateAway}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight="600">
            {isCreate ? 'Create New Syllabus' : `Edit Syllabus: ${syllabusData.name}`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={handleNavigateAway}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSave}
            disabled={saving || !syllabusData.name.trim()}
            startIcon={saving && <CircularProgress size={20} color="inherit" />}
          >
            {saving ? 'Saving...' : 'Save Syllabus'}
          </Button>
          {isModified && (
            <Typography variant="caption" color="warning.main" sx={{ alignSelf: 'center' }}>
              * Unsaved changes
            </Typography>
          )}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Left column: Basic info */}
          <Grid item xs={12} md={4}>
            <StyledPaper>
              <Typography variant="h6" sx={{ mb: 2 }}>Basic Information</Typography>
              <TextField
                label="Syllabus Name"
                name="name"
                value={syllabusData.name}
                onChange={handleInputChange}
                fullWidth
                required
                placeholder="Enter syllabus name"
                sx={{ mb: 2 }}
              />
              <TextField
                label="Description"
                name="description"
                value={syllabusData.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={4}
                placeholder="Enter a brief description of this syllabus"
              />
              
              {/* Add Level section */}
              <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #eee' }}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Add New Level
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    size="small"
                    label="New Level Name"
                    value={newLevelName}
                    onChange={(e) => setNewLevelName(e.target.value)}
                    variant="outlined"
                    placeholder="Enter level name"
                    fullWidth
                  />
                  <Button 
                    variant="contained" 
                    startIcon={<AddIcon />}
                    onClick={handleAddLevel}
                    disabled={!newLevelName.trim()}
                  >
                    Add
                  </Button>
                </Box>
              </Box>
            </StyledPaper>
          </Grid>
          
          {/* Right column: Levels structure */}
          <Grid item xs={12} md={8}>
            <StyledPaper>
              <Typography variant="h6" sx={{ mb: 2 }}>Syllabus Structure</Typography>
              
              {syllabusData.levels.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center', backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography color="text.secondary">
                    No levels added yet. Add a level from the left panel to start creating your syllabus structure.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {syllabusData.levels.map((level) => (
                    <Paper 
                      key={level.id} 
                      elevation={0}
                      sx={{ 
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                      }}
                    >
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        mb: expandedLevel === level.id ? 2 : 0,
                        pb: 1,
                        borderBottom: expandedLevel === level.id ? '1px solid #f0f0f0' : 'none',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleLevelExpand(level.id)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton 
                            size="small" 
                            sx={{ 
                              p: 0.5,
                              transform: expandedLevel === level.id ? 'rotate(0deg)' : 'rotate(-90deg)',
                              transition: 'transform 0.2s'
                            }}
                          >
                            {expandedLevel === level.id ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
                          </IconButton>
                          <Typography variant="subtitle1" fontWeight="600" color="primary">
                            Level: {level.name}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            variant="outlined"
                            onClick={(e) => {
                              e.stopPropagation();
                              setNewSession({ levelId: level.id, name: '' });
                            }}
                          >
                            Add Session
                          </Button>
                          <IconButton 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteLevel(level.id);
                            }}
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>

                      {expandedLevel === level.id && (
                        <>
                          {/* Session input field */}
                          {newSession?.levelId === level.id && (
                            <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                              <TextField
                                size="small"
                                placeholder="New session name"
                                value={newSession.name}
                                onChange={(e) => setNewSession({ ...newSession, name: e.target.value })}
                                fullWidth
                              />
                              <Button 
                                variant="contained" 
                                size="small"
                                onClick={() => handleAddSession(level.id)}
                                disabled={!newSession.name.trim()}
                              >
                                Add
                              </Button>
                              <Button 
                                size="small"
                                onClick={() => setNewSession(null)}
                              >
                                Cancel
                              </Button>
                            </Box>
                          )}

                          {/* Sessions, Topics, Subtopics grid layout */}
                          {level.sessions.length === 0 ? (
                            <Box sx={{ py: 2, textAlign: 'center', backgroundColor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
                              <Typography color="text.secondary" variant="body2">
                                No sessions added to this level yet.
                              </Typography>
                            </Box>
                          ) : (
                            <Grid container spacing={2}>
                              {level.sessions.map((session) => (
                                <Grid item xs={12} key={session.id}>
                                  <Paper 
                                    elevation={0}
                                    sx={{ 
                                      p: 2, 
                                      border: '1px solid #e0e0e0',
                                      borderLeft: '4px solid #5c6bc0',
                                      borderRadius: 1
                                    }}
                                  >
                                    <Box sx={{ 
                                      display: 'flex', 
                                      justifyContent: 'space-between', 
                                      alignItems: 'center',
                                      mb: 1.5
                                    }}>
                                      <Typography variant="subtitle2" fontWeight="600">
                                        Session: {session.name}
                                      </Typography>
                                      <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                          size="small"
                                          startIcon={<AddIcon />}
                                          variant="outlined"
                                          onClick={() => setNewTopic({ 
                                            levelId: level.id, 
                                            sessionId: session.id, 
                                            name: '' 
                                          })}
                                          sx={{ fontSize: '0.75rem' }}
                                        >
                                          Add Topic
                                        </Button>
                                        <IconButton 
                                          size="small" 
                                          onClick={() => handleDeleteSession(level.id, session.id)}
                                          sx={{ color: 'error.main' }}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    </Box>

                                    {/* Topic input field */}
                                    {newTopic?.levelId === level.id && newTopic?.sessionId === session.id && (
                                      <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                                        <TextField
                                          size="small"
                                          placeholder="New topic name"
                                          value={newTopic.name}
                                          onChange={(e) => setNewTopic({ ...newTopic, name: e.target.value })}
                                          fullWidth
                                        />
                                        <Button 
                                          variant="contained" 
                                          size="small"
                                          onClick={() => handleAddTopic(level.id, session.id)}
                                          disabled={!newTopic.name.trim()}
                                        >
                                          Add
                                        </Button>
                                        <Button 
                                          size="small"
                                          onClick={() => setNewTopic(null)}
                                        >
                                          Cancel
                                        </Button>
                                      </Box>
                                    )}

                                    {/* Topics grid */}
                                    {session.topics.length === 0 ? (
                                      <Box sx={{ py: 1.5, textAlign: 'center', backgroundColor: '#f5f5f5', borderRadius: 1, mb: 1 }}>
                                        <Typography color="text.secondary" variant="body2" fontSize="0.875rem">
                                          No topics added to this session yet.
                                        </Typography>
                                      </Box>
                                    ) : (
                                      <Grid container spacing={2}>
                                        {session.topics.map((topic) => (
                                          <Grid item xs={12} sm={6} key={topic.id}>
                                            <Paper 
                                              elevation={0}
                                              sx={{ 
                                                p: 1.5, 
                                                border: '1px solid #e0e0e0',
                                                borderLeft: '3px solid #7986cb',
                                                borderRadius: 1
                                              }}
                                            >
                                              <Box sx={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between', 
                                                alignItems: 'center',
                                                mb: 1
                                              }}>
                                                <Typography variant="body2" fontWeight="600">
                                                  {topic.name}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                  <IconButton 
                                                    size="small" 
                                                    onClick={() => handleDeleteTopic(level.id, session.id, topic.id)}
                                                    sx={{ 
                                                      color: 'error.main', 
                                                      p: 0.5
                                                    }}
                                                  >
                                                    <DeleteIcon sx={{ fontSize: '1rem' }} />
                                                  </IconButton>
                                                </Box>
                                              </Box>

                                              {/* Subtopic input field */}
                                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                                                <TextField
                                                  size="small"
                                                  placeholder="Add subtopic..."
                                                  value={
                                                    newSubtopic?.levelId === level.id && 
                                                    newSubtopic?.sessionId === session.id && 
                                                    newSubtopic?.topicId === topic.id ? 
                                                      newSubtopic.name : ''
                                                  }
                                                  onChange={(e) => setNewSubtopic({ 
                                                    levelId: level.id, 
                                                    sessionId: session.id, 
                                                    topicId: topic.id, 
                                                    name: e.target.value 
                                                  })}
                                                  variant="outlined"
                                                  fullWidth
                                                  inputProps={{ style: { fontSize: '0.875rem', padding: '8px 10px' } }}
                                                />
                                                <Button 
                                                  variant="text" 
                                                  size="small"
                                                  disabled={
                                                    !newSubtopic?.name?.trim() || 
                                                    newSubtopic.levelId !== level.id || 
                                                    newSubtopic.sessionId !== session.id ||
                                                    newSubtopic.topicId !== topic.id
                                                  }
                                                  onClick={() => handleAddSubtopic(level.id, session.id, topic.id)}
                                                  sx={{ minWidth: 'auto', p: 0.5 }}
                                                >
                                                  <AddIcon fontSize="small" />
                                                </Button>
                                              </Box>

                                              {/* Subtopics */}
                                              {topic.subtopics.length > 0 ? (
                                                <Box sx={{ 
                                                  bgcolor: '#f8f8f8', 
                                                  p: 1, 
                                                  borderRadius: 1, 
                                                  maxHeight: '120px', 
                                                  overflowY: 'auto'
                                                }}>
                                                  <Typography variant="caption" color="text.secondary" sx={{ pl: 1, mb: 0.5, display: 'block' }}>
                                                    Subtopics:
                                                  </Typography>
                                                  {topic.subtopics.map((subtopic) => (
                                                    <Box 
                                                      key={subtopic.id}
                                                      sx={{ 
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        py: 0.5,
                                                        px: 1,
                                                        borderRadius: 1,
                                                        '&:hover': {
                                                          bgcolor: '#eeeeee',
                                                          '& .delete-icon': {
                                                            visibility: 'visible'
                                                          }
                                                        }
                                                      }}
                                                    >
                                                      <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                          fontSize: '0.75rem',
                                                          overflow: 'hidden',
                                                          textOverflow: 'ellipsis',
                                                          maxWidth: '140px',
                                                          whiteSpace: 'nowrap'
                                                        }}
                                                      >
                                                        • {subtopic.name}
                                                      </Typography>
                                                      <IconButton 
                                                        size="small"
                                                        onClick={() => handleDeleteSubtopic(level.id, session.id, topic.id, subtopic.id)}
                                                        sx={{ 
                                                          color: 'error.main', 
                                                          p: 0.2,
                                                          visibility: 'hidden'
                                                        }}
                                                        className="delete-icon"
                                                      >
                                                        <DeleteIcon sx={{ fontSize: '0.875rem' }} />
                                                      </IconButton>
                                                    </Box>
                                                  ))}
                                                </Box>
                                              ) : (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 1 }}>
                                                  No subtopics added
                                                </Typography>
                                              )}
                                            </Paper>
                                          </Grid>
                                        ))}
                                      </Grid>
                                    )}
                                  </Paper>
                                </Grid>
                              ))}
                            </Grid>
                          )}
                        </>
                      )}
                    </Paper>
                  ))}
                </Box>
              )}
            </StyledPaper>
          </Grid>
        </Grid>
      )}
    </Container>
  );
} 